import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import clsx from "clsx";

const THEME_EMOJI = {
  winter:    "❄️",
  spring:    "🌸",
  summer:    "☀️",
  halloween: "🎃",
  custom:    "🎉",
};

// ── Challenge progress bar ────────────────────────────────────────────────────
function ChallengeBar({ challenge }) {
  const p      = challenge.myProgress;
  const cur    = p?.progress   || 0;
  const done   = p?.completed  || false;
  const claimed= p?.rewardClaimed || false;
  const pct    = Math.min(100, Math.round((cur / challenge.targetValue) * 100));

  return (
    <div className={clsx(
      "relative p-3 rounded-xl border transition-all duration-200",
      done && !claimed  ? "border-green-500/40 bg-green-500/5"
      : done && claimed ? "border-white/10 bg-white/[0.02] opacity-60"
      : "border-nexus-border bg-nexus-s2"
    )}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="font-bold text-xs">{challenge.title}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{challenge.description}</p>
        </div>
        {/* Rewards */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          {challenge.rewardXP > 0 && (
            <span className="text-[9px] font-black text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded-md">+{challenge.rewardXP}XP</span>
          )}
          {challenge.rewardCoins > 0 && (
            <span className="text-[9px] font-black text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-md">+{challenge.rewardCoins}🪙</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(139,92,246,0.12)" }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: done ? "linear-gradient(90deg,#22c55e,#16a34a)" : "linear-gradient(90deg,#8b5cf6,#06b6d4)",
              boxShadow: done ? "0 0 6px rgba(34,197,94,0.5)" : "0 0 6px rgba(139,92,246,0.5)",
            }}
          />
        </div>
        <span className="text-[9px] font-bold text-gray-400 shrink-0">{cur}/{challenge.targetValue}</span>
        {done && claimed && <span className="text-[9px] text-gray-600">✓ Erhalten</span>}
        {done && !claimed && (
          <span className="text-[9px] font-black text-green-400 animate-pulse">Bereit!</span>
        )}
      </div>
    </div>
  );
}

// ── Single event card ─────────────────────────────────────────────────────────
function EventCard({ event, onClaim, claimingId }) {
  const qc = useQueryClient();

  const now      = new Date();
  const endsAt   = new Date(event.endsAt);
  const msLeft   = endsAt - now;
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));

  const claimable = event.challenges.filter(
    (c) => c.myProgress?.completed && !c.myProgress?.rewardClaimed
  );

  const totalChallenges = event.challenges.length;
  const doneChallenges  = event.challenges.filter((c) => c.myProgress?.completed).length;
  const eventPct        = totalChallenges > 0 ? Math.round((doneChallenges / totalChallenges) * 100) : 0;

  return (
    <div
      className="relative rounded-2xl border overflow-hidden"
      style={{
        borderColor: `${event.bannerColor}40`,
        background: `linear-gradient(135deg, ${event.bannerColor}12 0%, rgba(13,13,43,0.9) 60%)`,
        boxShadow: `0 4px 30px ${event.bannerColor}20`,
      }}
    >
      {/* Top gradient accent */}
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
        style={{ background: `linear-gradient(90deg, transparent, ${event.bannerColor}, transparent)` }}/>

      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-2xl">{event.emoji || THEME_EMOJI[event.theme] || "🎉"}</span>
              <div>
                <p className="font-black text-sm leading-tight">{event.name}</p>
                <p className="text-[10px] text-gray-500">{event.description}</p>
              </div>
            </div>
          </div>
          <div className="shrink-0 text-right">
            {event.xpMultiplier > 1 && (
              <div className="text-[9px] font-black px-2 py-1 rounded-lg mb-1"
                style={{ background: `${event.bannerColor}25`, color: event.bannerColor, border: `1px solid ${event.bannerColor}40` }}>
                ×{event.xpMultiplier} XP
              </div>
            )}
            <p className="text-[9px] text-gray-500">
              {daysLeft === 0 ? "Endet heute" : `Noch ${daysLeft} Tag${daysLeft !== 1 ? "e" : ""}`}
            </p>
          </div>
        </div>

        {/* Event progress */}
        {totalChallenges > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-[9px] mb-1">
              <span className="text-gray-500">{doneChallenges}/{totalChallenges} Challenges</span>
              <span className="font-bold" style={{ color: event.bannerColor }}>{eventPct}%</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${eventPct}%`, background: event.bannerColor, boxShadow: `0 0 8px ${event.bannerColor}70` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Claim bar */}
      {claimable.length > 0 && (
        <div className="mx-4 mb-3 p-3 rounded-xl border border-green-500/30 bg-green-500/5">
          <p className="text-[10px] font-black text-green-400 mb-2">
            🎁 {claimable.length} Belohnung{claimable.length !== 1 ? "en" : ""} verfügbar!
          </p>
          <div className="flex flex-wrap gap-2">
            {claimable.map((c) => (
              <button
                key={c.id}
                onClick={() => onClaim(c.id)}
                disabled={claimingId === c.id}
                className="text-[10px] font-black px-3 py-1.5 rounded-lg bg-green-500/20 text-green-300 border border-green-500/40 hover:bg-green-500/30 transition-all disabled:opacity-50"
              >
                {claimingId === c.id ? "..." : `${c.title} einlösen`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Challenges list */}
      {event.challenges.length > 0 && (
        <div className="px-4 pb-4 space-y-2">
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Challenges</p>
          {event.challenges.map((c) => (
            <ChallengeBar key={c.id} challenge={c} />
          ))}
        </div>
      )}

      {event.challenges.length === 0 && (
        <div className="px-4 pb-4 text-center">
          <p className="text-[10px] text-gray-600">Keine Challenges konfiguriert.</p>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SeasonalEvents() {
  const qc = useQueryClient();
  const [claimingId, setClaimingId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["gamification-events"],
    queryFn:  () => api.get("/gamification/events").then((r) => r.data),
    refetchInterval: 60_000,
  });

  const claim = useMutation({
    mutationFn: (challengeId) => api.post(`/gamification/events/claim/${challengeId}`),
    onMutate:   (id) => setClaimingId(id),
    onSettled:  ()   => setClaimingId(null),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["gamification-events"] }),
  });

  if (isLoading) return (
    <div className="flex justify-center pt-20">
      <div className="w-10 h-10 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-transparent border-l-transparent animate-spin"/>
    </div>
  );

  const events = data?.events || [];

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-2 relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(251,191,36,0.06) 0%, transparent 70%)" }}/>
        <div className="text-5xl mb-3 inline-block animate-float"
          style={{ filter: "drop-shadow(0 0 22px rgba(251,191,36,0.5))" }}>🎪</div>
        <h1 className="text-2xl font-black">Saisonale Events</h1>
        <p className="text-xs text-gray-500 mt-1">Zeitlich begrenzte Challenges &amp; Belohnungen</p>
      </div>

      {/* ── No active events ────────────────────────────────────────────────── */}
      {events.length === 0 && (
        <div className="p-10 rounded-2xl border border-nexus-border text-center"
          style={{ background: "rgba(13,13,43,0.5)" }}>
          <div className="text-5xl mb-3">😴</div>
          <p className="font-black text-lg">Kein aktives Event</p>
          <p className="text-xs text-gray-500 mt-1">Schau später wieder vorbei — Events werden regelmäßig gestartet.</p>
        </div>
      )}

      {/* ── Events ──────────────────────────────────────────────────────────── */}
      {events.map((ev) => (
        <EventCard
          key={ev.id}
          event={ev}
          onClaim={(challengeId) => claim.mutate(challengeId)}
          claimingId={claimingId}
        />
      ))}

      <div className="pb-2"/>
    </div>
  );
}

